/**
 * Chat API Endpoints
 * SSE-based streaming chat with thinking steps
 */

import { postSse, type SseEvent } from '@/lib/sse'
import { env } from '@/lib/env'
import { http } from '@/lib/http'
import { getAuthItem } from '@/lib/authStorage'
import { getActiveWorkspaceId } from '@/lib/api/workspace-context'
import { parseSSEText } from './parseSSEText'
import type {
  ChatEvent,
  ChatRequest,
  ChatCompletionResponse,
  ServerChatMessage,
  ChatSessionSummary,
  GreetingEventPayload,
  GreetingRequest,
} from './chat.types'

// Re-export so the barrel surface (`@/lib/api/endpoints/chat`) stays stable.
// Canonical definitions live in `./chat.types` and `./parseSSEText`.
export type {
  ChatUIAction,
  ChatEvent,
  ChatMessage,
  ChatRequest,
  ChatCompletionResponse,
  ServerChatMessageSource,
  ServerChatMessage,
  ChatSessionSummary,
  GreetingConnectedPayload,
  GreetingTextPayload,
  GreetingCompletePayload,
  GreetingErrorPayload,
  GreetingEventPayload,
  GreetingRequest,
} from './chat.types'
export { parseSSEText } from './parseSSEText'

/**
 * Open a streaming chat session
 * Returns an async generator that yields SSE events
 *
 * @example
 * ```ts
 * for await (const event of openChatStream(token, {
 *   messages: [{ role: 'user', content: 'Hello' }]
 * })) {
 *   if (event.data.type === 'text') {
 *     console.log('AI text:', event.data.text)
 *   }
 * }
 * ```
 */

/**
 * Raw, tool-free LLM completion — honors the caller's `systemPrompt` with NO
 * agent loop, pre-search, or tools (hits x-agent `/complete`, not `/chat`).
 * Used by the note editor's inline assistant, which needs a note-scoped
 * completion; the agent `/chat` route ignores `systemPrompt` and runs the full
 * general agent, so it can't be note-scoped.
 */
export async function completeNote(
  authToken: string | null,
  body: { systemPrompt: string; messages: Array<{ role: string; content: string }>; maxTokens?: number; userId?: string },
): Promise<string> {
  // /complete is workspace-scoped on the server (auth + membership + usage
  // attribution), so it needs X-Workspace-Id like every other agent call —
  // openChatStream sends it too. Without it the route 400s; without X-User-Id
  // membership enforcement can't match the caller.
  const workspaceId = getActiveWorkspaceId()
  const { userId, ...payload } = body
  const res = await fetch(`${env.chatBaseUrl}/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(workspaceId ? { 'X-Workspace-Id': workspaceId } : {}),
      ...(userId ? { 'X-User-Id': userId } : {}),
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    // Surface the server's error message ({error,message}) instead of a bare
    // status, so note-chat failures are diagnosable.
    let detail = ''
    try {
      const err = await res.json()
      detail = err?.message || err?.error || ''
    } catch {
      detail = (await res.text().catch(() => '')) || ''
    }
    throw new Error(`completeNote failed: ${res.status}${detail ? ` — ${detail}` : ''}`)
  }
  // A proxy/maintenance page can return 200 with non-JSON; guard so it surfaces
  // as a clean error rather than an opaque SyntaxError.
  let data: { content?: unknown }
  try {
    data = await res.json()
  } catch {
    throw new Error('completeNote failed: response was not valid JSON')
  }
  return (typeof data?.content === 'string' ? data.content : '')
}

export async function* openChatStream(
  authToken: string | null,
  request: ChatRequest,
  sessionId?: string,
  signal?: AbortSignal
): AsyncGenerator<SseEvent<ChatEvent>, void, unknown> {
  // Use chatBaseUrl which routes to the agent service
  // In Docker: same gateway, nginx routes /chat to x-agent
  // In hybrid dev: directly to x-agent on port 8300
  const url = `${env.chatBaseUrl}/chat`

  const conversationId = request.conversationId
  const workspaceId = request.workspaceId || getActiveWorkspaceId()

  const headers: Record<string, string> = {
    'X-Workspace-Id': workspaceId,
  }

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }

  if (request.guestId) {
    headers['X-Guest-Id'] = request.guestId
  }

  if (request.userId) {
    headers['X-User-Id'] = request.userId
  }

  if (conversationId) {
    headers['X-Conversation-Id'] = conversationId
  }

  if (sessionId) {
    headers['X-Session-ID'] = sessionId
  }

  // Add X-User-Email header for supervisor routing
  const userEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null
  if (userEmail) {
    headers['X-User-Email'] = userEmail
  }

  const payload = {
    ...request,
    conversationId,
    workspaceId,
  }

  for await (const event of postSse<ChatEvent>(url, payload, headers, signal)) {
    yield event
  }
}

/**
 * Non-streaming chat (JSON response).
 * Useful as a fallback when SSE fails.
 */
export async function postChat(
  authToken: string | null,
  request: ChatRequest,
  sessionId?: string,
  signal?: AbortSignal,
  onProgress?: (status: string) => void
): Promise<ChatCompletionResponse> {
  const url = `${env.chatBaseUrl}/chat`
  const conversationId = request.conversationId
  const workspaceId = request.workspaceId || getActiveWorkspaceId()

  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Workspace-Id': workspaceId,
  }

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }

  if (request.guestId) {
    headers['X-Guest-Id'] = request.guestId
  }

  if (request.userId) {
    headers['X-User-Id'] = request.userId
  }

  if (conversationId) {
    headers['X-Conversation-Id'] = conversationId
  }

  if (sessionId) {
    headers['X-Session-ID'] = sessionId
  }

  const userEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null
  if (userEmail) {
    headers['X-User-Email'] = userEmail
  }

  const fetchOptions: RequestInit = {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...request, conversationId, workspaceId, stream: false }),
    credentials: 'include',
    signal,
  }

  const response = await fetch(url, fetchOptions)

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Chat request failed: ${response.status} ${response.statusText} ${errorText}`)
  }

  // Get response as text first to handle both JSON and SSE responses.
  // The agent gateway may return SSE even when stream:false is requested,
  // so on native (CapacitorHttp) we may receive raw SSE text instead of JSON.
  const responseText = await response.text()

  // Try JSON first (normal buffered JSON response)
  try {
    return JSON.parse(responseText) as ChatCompletionResponse
  } catch {
    // Not JSON — likely SSE text from agent. Parse SSE events to extract content.
  }

  return parseSSEText(responseText, onProgress)
}

/**
 * Get chat messages for a conversation.
 */
export async function getConversationChatMessages(
  authToken: string | null,
  conversationId: string,
  options?: {
    sessionId?: string
    limit?: number
    includeSummarized?: boolean
  }
): Promise<{ messages: ServerChatMessage[]; session: any | null }> {
  const workspaceId = getActiveWorkspaceId()
  const searchParams: Record<string, string> = {}
  if (options?.sessionId) searchParams.sessionId = options.sessionId
  if (options?.limit) searchParams.limit = options.limit.toString()
  if (options?.includeSummarized) searchParams.includeSummarized = 'true'

  const response = await http.get<{ messages: ServerChatMessage[]; session: any }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/messages`,
    {
      authToken,
      searchParams,
    }
  )

  return response.data
}

/**
 * Save a chat message to the server
 */
export async function saveConversationChatMessage(
  authToken: string | null,
  conversationId: string,
  message: {
    role: 'user' | 'assistant'
    content: string
    mode?: 'local' | 'api'
    durationMs?: number
    routing?: string
    thinking?: string
    toolsUsed?: number
    toolCalls?: any[]
    toolResults?: any[]
    /** Per-message attachments persisted alongside the row so prior turns
     *  can be reconstructed with their file context intact. Without this,
     *  follow-up turns lose all attachment metadata and the agent re-asks
     *  the user to upload files they already sent. */
    attachments?: Array<{ filename: string; fileType: string; assetId?: string; url?: string }>
  }
): Promise<ServerChatMessage> {
  const workspaceId = getActiveWorkspaceId()
  const response = await http.post<ServerChatMessage>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/messages`,
    message,
    { authToken }
  )

  return response.data
}

/**
 * Clear chat session and start fresh
 */
export async function clearChatSession(
  authToken: string | null,
  conversationId: string
): Promise<void> {
  const workspaceId = getActiveWorkspaceId()
  // Create a fresh active session (archives prior active session)
  await http.post(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/sessions`,
    {},
    { authToken }
  )
}

/**
 * Get chat sessions for a specific conversation.
 */
export async function getConversationChatSessions(
  authToken: string | null,
  conversationId: string,
  options?: {
    limit?: number
  }
): Promise<ChatSessionSummary[]> {
  const workspaceId = getActiveWorkspaceId()
  const searchParams: Record<string, string> = {}
  if (options?.limit) searchParams.limit = options.limit.toString()

  const response = await http.get<{ sessions: ChatSessionSummary[] }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/conversations/${encodeURIComponent(conversationId)}/sessions`,
    {
      authToken,
      searchParams,
    }
  )

  return response.data.sessions
}

/**
 * Open a streaming greeting session
 * Returns an async generator that yields SSE events
 *
 * @example
 * ```ts
 * for await (const event of openGreetingStream(token, {
 *   conversationId: 'conv-123',
 *   timezone: 'America/Los_Angeles',
 *   isReturning: false,
 * })) {
 *   if (event.type === 'text') {
 *     const data = event.data as GreetingTextPayload
 *     console.log('Greeting:', data.content)
 *   }
 * }
 * ```
 */
export async function* openGreetingStream(
  authToken: string | null,
  request: GreetingRequest,
  signal?: AbortSignal
): AsyncGenerator<SseEvent<GreetingEventPayload>, void, unknown> {
  const url = `${env.chatBaseUrl}/chat/greeting`

  const conversationId = request.conversationId
  const workspaceId = request.workspaceId || getActiveWorkspaceId()

  const headers: Record<string, string> = {
    'X-Workspace-Id': workspaceId,
  }

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`
  }

  if (conversationId) {
    headers['X-Conversation-Id'] = conversationId
  }

  // Add X-User-Email header for supervisor routing
  const userEmail = typeof window !== 'undefined' ? getAuthItem('userEmail') : null
  if (userEmail) {
    headers['X-User-Email'] = userEmail
  }

  const payload = {
    ...request,
    conversationId,
    workspaceId,
  }

  for await (const event of postSse<GreetingEventPayload>(url, payload, headers, signal)) {
    yield event
  }
}

/**
 * Cancel an in-flight page/report build.
 *
 * Best-effort and idempotent — calls x-agent's BuildRegistry to abort the
 * AbortController for this moduleId. Returns true if the build was cancelled,
 * false if there was no in-flight build (404), the request timed out, or the
 * endpoint isn't available (older agent that pre-dates #1817 fix).
 *
 * Callers should treat any return value as "safe to proceed with delete":
 * if there was no build to cancel, the row was never being re-upserted in
 * the first place.
 */
export async function cancelBuild(
  moduleId: string,
  opts?: { authToken?: string | null; timeoutMs?: number; workspaceId?: string },
): Promise<boolean> {
  const timeoutMs = opts?.timeoutMs ?? 500
  const url = `${env.chatBaseUrl}/chat/cancel-build/${encodeURIComponent(moduleId)}`

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    // Caller can pass explicit workspaceId to avoid a container-swap race;
    // falls back to getActiveWorkspaceId().
    const activeWorkspaceId = opts?.workspaceId || getActiveWorkspaceId()
    const headers: Record<string, string> = {
      'X-Workspace-Id': activeWorkspaceId,
    }
    if (opts?.authToken) headers['Authorization'] = `Bearer ${opts.authToken}`
    if (env.defaultUserId) headers['X-User-Id'] = env.defaultUserId
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers,
      signal: controller.signal,
    })
    return res.status === 200
  } catch {
    // Timeout, network error, or older agent without the endpoint.
    // Caller proceeds with DELETE either way — the API defense-in-depth
    // keeps UX correct.
    return false
  } finally {
    clearTimeout(timer)
  }
}
